import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { api } from '../lib/api'
import type { ProfileListItem, JobListItem } from '../types/api'
import Card from '../components/ui/Card'

export default function Dashboard() {
  const navigate = useNavigate()
  const [profiles, setProfiles] = useState<ProfileListItem[]>([])
  const [jobs, setJobs] = useState<JobListItem[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    Promise.all([api.profiles.list(), api.jobs.list()])
      .then(([p, j]) => {
        setProfiles(p.data)
        setJobs(j.data)
      })
      .finally(() => setLoading(false))
  }, [])

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse">
          <div className="h-7 w-32 bg-surface-lowest rounded" />
          <div className="h-4 w-48 bg-surface-lowest rounded mt-2" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[1, 2, 3].map((i) => <div key={i} className="h-24 bg-surface-lowest rounded-lg" />)}
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-10">
      <div>
        <p className="text-sm text-text-muted mb-1">Overview</p>
        <h1 className="text-2xl font-bold text-foreground">Dashboard</h1>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-xl">
        <Card>
          <p className="text-sm text-text-muted">Total candidates</p>
          <p className="text-3xl font-bold text-primary mt-1">
            {profiles.length}
          </p>
        </Card>
        <Card>
          <p className="text-sm text-text-muted">Active jobs</p>
          <p className="text-3xl font-bold text-foreground mt-1">
            {jobs.filter((j) => j.status === 'Open').length}
          </p>
        </Card>
        <Card>
          <p className="text-sm text-text-muted">System status</p>
          <p className="text-lg font-medium text-accent-success mt-2">Operational</p>
        </Card>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <div className="flex items-center justify-between mb-4">
            <p className="text-sm font-medium text-foreground">Recent candidates</p>
            <button
              onClick={() => navigate('/admin/applicants')}
              className="text-sm text-primary hover:underline"
            >
              View all →
            </button>
          </div>
          {profiles.length === 0 ? (
            <p className="text-text-muted text-sm">No candidates yet.</p>
          ) : (
            <ul className="divide-y divide-border-subtle">
              {profiles.slice(0, 5).map((p) => (
                <li
                  key={p.id}
                  onClick={() => navigate(`/admin/applicants/${p.id}`)}
                  className="flex items-center justify-between py-3 first:pt-0 last:pb-0 cursor-pointer hover:text-primary transition-colors"
                >
                  <span className="text-sm">{p.name}</span>
                  <span className="text-xs text-text-muted">{p.skills.length} skills</span>
                </li>
              ))}
            </ul>
          )}
        </Card>

        <Card>
          <div className="flex items-center justify-between mb-4">
            <p className="text-sm font-medium text-foreground">Recent jobs</p>
            <button
              onClick={() => navigate('/admin/jobs')}
              className="text-sm text-primary hover:underline"
            >
              View all →
            </button>
          </div>
          {jobs.length === 0 ? (
            <p className="text-text-muted text-sm">No jobs yet.</p>
          ) : (
            <ul className="divide-y divide-border-subtle">
              {jobs.slice(0, 5).map((j) => (
                <li
                  key={j.id}
                  onClick={() => navigate(`/admin/jobs/${j.id}`)}
                  className="flex items-center justify-between py-3 first:pt-0 last:pb-0 cursor-pointer hover:text-primary transition-colors"
                >
                  <span className="text-sm">{j.title}</span>
                  <span className="text-xs text-text-muted">{j.employer}</span>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>
    </div>
  )
}
